/**
 * @file useExportImport.js
 * @description TTL export and import operations for BRICK Schema
 *
 * Handles:
 * - TTL file download
 * - TTL preview/viewing
 * - TTL URL generation
 * - Import validation (future)
 */

import { ref, toValue } from 'vue';
import { useQuasar } from 'quasar';
import { api } from '@/services/api';

export function useExportImport(siteId, siteName) {
  // ============================================================================
  // STATE
  // ============================================================================

  const $q = useQuasar();

  /**
   * TTL download loading state
   * @type {Ref<boolean>}
   */
  const downloadingTTL = ref(false);

  /**
   * TTL preview content
   * @type {Ref<string>}
   */
  const ttlPreview = ref('');

  /**
   * TTL preview dialog visibility
   * @type {Ref<boolean>}
   */
  const ttlPreviewDialogVisible = ref(false);

  /**
   * Loading state for TTL preview
   * @type {Ref<boolean>}
   */
  const loadingTTLPreview = ref(false);

  /**
   * Export statistics
   * @type {Ref<Object>}
   */
  const exportStats = ref({
    buildings: 0,
    spaces: 0,
    equipment: 0,
    devices: 0,
    points: 0,
    relationships: 0,
  });

  // ============================================================================
  // TTL EXPORT METHODS
  // ============================================================================

  /**
   * Download TTL file for the site
   * Uses v2 API endpoint that returns JSON with TTL content
   *
   * @returns {Promise<void>}
   */
  async function downloadTTL() {
    const unwrappedSiteId = toValue(siteId);
    const unwrappedSiteName = toValue(siteName);

    if (!unwrappedSiteId) {
      $q.notify({
        type: 'warning',
        message: 'No site selected',
      });
      return;
    }

    downloadingTTL.value = true;

    try {

      // Use v2 API endpoint with authenticated API service
      const result = await api.get(`/v2/sites/${unwrappedSiteId}/brick/ttl`);

      if (!result.ttl && !result.data?.ttl) {
        throw new Error('Invalid TTL response from server');
      }

      // Handle both unwrapped response and wrapped response
      const ttlData = result.ttl ? result : result.data;

      // Create blob from TTL text content
      const blob = new Blob([ttlData.ttl], { type: 'text/turtle' });
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const a = document.createElement('a');
      a.href = url;
      a.download = `${sanitizeFilename(unwrappedSiteName)}_brick_schema.ttl`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up blob URL
      window.URL.revokeObjectURL(url);

      // Update stats if provided
      if (ttlData.stats) {
        exportStats.value = ttlData.stats;
      }

      $q.notify({
        type: 'positive',
        message: 'TTL file downloaded successfully',
        caption: `${unwrappedSiteName}_brick_schema.ttl`,
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to download TTL: ${error.message}`,
      });
    } finally {
      downloadingTTL.value = false;
    }
  }

  /**
   * Show raw TTL in preview dialog
   * Loads TTL content without downloading
   *
   * @returns {Promise<void>}
   */
  async function showRawTTL() {
    const unwrappedSiteId = toValue(siteId);

    if (!unwrappedSiteId) {
      $q.notify({
        type: 'warning',
        message: 'No site selected',
      });
      return;
    }

    loadingTTLPreview.value = true;
    ttlPreviewDialogVisible.value = true;

    try {

      // Use authenticated API service
      const result = await api.get(`/v2/sites/${unwrappedSiteId}/brick/ttl`);

      if (!result.ttl && !result.data?.ttl) {
        throw new Error('Invalid TTL response from server');
      }

      // Handle both unwrapped response and wrapped response
      const ttlData = result.ttl ? result : result.data;

      ttlPreview.value = ttlData.ttl;

      // Update stats if provided
      if (ttlData.stats) {
        exportStats.value = ttlData.stats;
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: `Failed to load TTL: ${error.message}`,
      });
      closeTTLPreview();
    } finally {
      loadingTTLPreview.value = false;
    }
  }

  /**
   * Close TTL preview dialog
   */
  function closeTTLPreview() {
    ttlPreviewDialogVisible.value = false;
    ttlPreview.value = '';
  }

  /**
   * Copy TTL content to clipboard from preview
   *
   * @returns {Promise<void>}
   */
  async function copyTTLContent() {
    if (!ttlPreview.value) {
      $q.notify({
        type: 'warning',
        message: 'No TTL content to copy',
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(ttlPreview.value);

      $q.notify({
        type: 'positive',
        message: 'TTL content copied to clipboard',
      });
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to copy to clipboard',
      });
    }
  }

  /**
   * Copy TTL API URL to clipboard
   * Useful for external tools that can consume TTL
   *
   * @returns {void}
   */
  function copyTTLURL() {
    const unwrappedSiteId = toValue(siteId);

    if (!unwrappedSiteId) {
      $q.notify({
        type: 'warning',
        message: 'No site selected',
      });
      return;
    }

    const url = `${window.location.origin}/api/v2/sites/${unwrappedSiteId}/brick/ttl`;

    navigator.clipboard
      .writeText(url)
      .then(() => {
        $q.notify({
          type: 'positive',
          message: 'TTL URL copied to clipboard',
          caption: url,
        });
      })
      .catch((error) => {
        $q.notify({
          type: 'negative',
          message: 'Failed to copy URL to clipboard',
        });
      });
  }

  // ============================================================================
  // TTL VALIDATION METHODS (Future)
  // ============================================================================

  /**
   * Validate TTL content
   * Checks for BRICK Schema compliance
   *
   * @param {string} ttlContent - TTL content to validate
   * @returns {Object} Validation result
   */
  function validateTTL(ttlContent) {
    // Could use BRICK Schema validator or basic syntax checks

    const errors = [];
    const warnings = [];

    // Basic checks
    if (!ttlContent || ttlContent.trim().length === 0) {
      errors.push('TTL content is empty');
    }

    // Check for required prefixes
    const requiredPrefixes = ['@prefix brick:', '@prefix rdf:', '@prefix rdfs:'];
    requiredPrefixes.forEach((prefix) => {
      if (!ttlContent.includes(prefix)) {
        warnings.push(`Missing recommended prefix: ${prefix}`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  // ============================================================================
  // IMPORT METHODS (Future Implementation)
  // ============================================================================

  /**
   * Import TTL file
   * Future: Allow importing BRICK Schema from TTL
   *
   * @param {File} file - TTL file to import
   * @returns {Promise<Object>} Import result
   */
  async function importTTL(file) {

    $q.notify({
      type: 'info',
      message: 'TTL import feature coming soon',
    });

    return {
      success: false,
      message: 'Not implemented',
    };
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Sanitize filename for download
   * Removes special characters and spaces
   *
   * @param {string} filename - Original filename
   * @returns {string} Sanitized filename
   */
  function sanitizeFilename(filename) {
    return filename
      .replace(/[^a-z0-9]/gi, '_') // Replace special chars with underscore
      .replace(/_+/g, '_') // Replace multiple underscores with single
      .toLowerCase();
  }

  /**
   * Format TTL content for display
   * Adds syntax highlighting hints (for future UI enhancement)
   *
   * @param {string} ttl - Raw TTL content
   * @returns {string} Formatted TTL
   */
  function formatTTLForDisplay(ttl) {
    // Basic formatting - could be enhanced with syntax highlighting
    return ttl;
  }

  /**
   * Get export statistics summary
   *
   * @returns {string} Human-readable summary
   */
  function getExportSummary() {
    const stats = exportStats.value;

    if (
      stats.buildings === 0 &&
      stats.spaces === 0 &&
      stats.equipment === 0 &&
      stats.devices === 0 &&
      stats.points === 0
    ) {
      return 'No export statistics available';
    }

    const parts = [];

    if (stats.buildings > 0) {parts.push(`${stats.buildings} building${stats.buildings !== 1 ? 's' : ''}`);}
    if (stats.spaces > 0) {parts.push(`${stats.spaces} space${stats.spaces !== 1 ? 's' : ''}`);}
    if (stats.equipment > 0) {parts.push(`${stats.equipment} equipment`);}
    if (stats.devices > 0) {parts.push(`${stats.devices} device${stats.devices !== 1 ? 's' : ''}`);}
    if (stats.points > 0) {parts.push(`${stats.points} point${stats.points !== 1 ? 's' : ''}`);}
    if (stats.relationships > 0) {parts.push(`${stats.relationships} relationship${stats.relationships !== 1 ? 's' : ''}`);}

    return `Exported: ${parts.join(', ')}`;
  }

  /**
   * Check if export is available
   * Ensures site has data to export
   *
   * @returns {boolean}
   */
  function canExport() {
    return !!toValue(siteId);
  }

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // State
    downloadingTTL,
    ttlPreview,
    ttlPreviewDialogVisible,
    loadingTTLPreview,
    exportStats,

    // Export methods
    downloadTTL,
    showRawTTL,
    closeTTLPreview,
    copyTTLContent,
    copyTTLURL,

    // Validation methods
    validateTTL,

    // Import methods (future)
    importTTL,

    // Utility methods
    sanitizeFilename,
    formatTTLForDisplay,
    getExportSummary,
    canExport,
  };
}
